import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import { ExamTopicGroupPriorityEnum, ExamTopicItemAnswerResultEnum } from '@dopingtech/live_expert_bff_api_ts';
import './topic-analysis-modal.scss';
import { Modal } from '@/components';
import LoadingSpinner from '@/components/loading-spinner';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useSizes } from '@/utility/responsive';

interface TopicAnalysisModalProps {
	isOpen: boolean;
	onClose: () => void;
	studentId: number;
	examId: number;
}

const MAX_STARS = 3;

const PRIORITY_STARS: Record<ExamTopicGroupPriorityEnum, number> = {
	[ExamTopicGroupPriorityEnum.Low]: 1,
	[ExamTopicGroupPriorityEnum.Medium]: 2,
	[ExamTopicGroupPriorityEnum.High]: 3
};

const ANSWER_CLASS: Record<ExamTopicItemAnswerResultEnum, string> = {
	[ExamTopicItemAnswerResultEnum.True]: 'correct',
	[ExamTopicItemAnswerResultEnum.False]: 'wrong',
	[ExamTopicItemAnswerResultEnum.Empty]: 'empty',
	[ExamTopicItemAnswerResultEnum.Unseen]: 'empty'
};

const LEGEND = [
	{ key: 'correct', label: 'Doğru' },
	{ key: 'wrong', label: 'Yanlış' },
	{ key: 'empty', label: 'Boş' }
];

const RING_RADIUS = 16;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const SuccessRing: React.FC<{ rate: number; wrongRatio: number }> = ({ rate, wrongRatio }) => {
	const success = (Math.min(Math.max(rate, 0), 100) / 100) * RING_CIRCUMFERENCE;
	const wrong = Math.min(wrongRatio * RING_CIRCUMFERENCE, RING_CIRCUMFERENCE - success);

	return (
		<div className="topic-analysis__ring">
			<svg viewBox="0 0 40 40">
				<circle className="topic-analysis__ring-track" cx="20" cy="20" r={RING_RADIUS} />
				<circle
					className="topic-analysis__ring-wrong"
					cx="20"
					cy="20"
					r={RING_RADIUS}
					strokeDasharray={`${wrong} ${RING_CIRCUMFERENCE}`}
					strokeDashoffset={-success}
				/>
				<circle
					className="topic-analysis__ring-success"
					cx="20"
					cy="20"
					r={RING_RADIUS}
					strokeDasharray={`${success} ${RING_CIRCUMFERENCE}`}
				/>
			</svg>
			<span className="topic-analysis__ring-label">%{Math.round(rate)}</span>
		</div>
	);
};

const TopicAnalysisModal: React.FC<TopicAnalysisModalProps> = ({ isOpen, onClose, studentId, examId }) => {
	const { isMobile } = useSizes();
	const { StudentExamControllerApi } = useApi();
	const [selectedLessonId, setSelectedLessonId] = useState<number>();
	const [sortAsc, setSortAsc] = useState<boolean | null>(null);

	const { data: lessons = [], isLoading: isLessonsLoading } = useQueryProvider(
		[QUERY_KEYS.findExamStatsByLessons, studentId, examId],
		() => StudentExamControllerApi.findExamStatsByLessons(studentId, examId),
		{
			select: ({ data }) => (data?.data ?? []).filter(lesson => lesson.lessonId !== undefined),
			enabled: isOpen,
			refetchOnWindowFocus: false
		}
	);

	useEffect(() => {
		if (selectedLessonId === undefined && lessons.length) setSelectedLessonId(lessons[0].lessonId);
	}, [lessons, selectedLessonId]);

	const { data: topics = [], isLoading: isTopicsLoading } = useQueryProvider(
		[QUERY_KEYS.findExamItemTopics, studentId, examId, selectedLessonId],
		() => StudentExamControllerApi.findExamItemTopics(studentId, examId, selectedLessonId as number),
		{
			select: ({ data }) => data?.data ?? [],
			enabled: isOpen && selectedLessonId !== undefined,
			refetchOnWindowFocus: false
		}
	);

	const sortedTopics = useMemo(() => {
		if (sortAsc === null) return topics;
		return [...topics].sort((a, b) => ((a.successRate ?? 0) - (b.successRate ?? 0)) * (sortAsc ? 1 : -1));
	}, [topics, sortAsc]);

	const isLoading = isLessonsLoading || isTopicsLoading;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size={isMobile ? 'full' : 'xl'}
			closeOnClickOutside
			closeOnEsc
			showCloseButton
			bodyClassName="topic-analysis"
		>
			<h3 className="topic-analysis__title">Konu Bazlı Analiz</h3>

			<If condition={lessons.length > 0}>
				<div className="topic-analysis__lessons">
					{lessons.map(lesson => (
						<button
							key={lesson.lessonId}
							type="button"
							className={clsx('topic-analysis__lesson', { 'topic-analysis__lesson--active': lesson.lessonId === selectedLessonId })}
							onClick={() => setSelectedLessonId(lesson.lessonId)}
						>
							{lesson.lessonName}
						</button>
					))}
				</div>
			</If>

			<Choose>
				<When condition={isLoading}>
					<LoadingSpinner loadingText={'Yükleniyor'} />
				</When>
				<When condition={!lessons.length || !sortedTopics.length}>
					<div className="topic-analysis__empty">
						Bu deneme sınavına ait konu analizi bulunmamaktadır.
					</div>
				</When>
				<Otherwise>
					<div className="topic-analysis__table">
						<div className="topic-analysis__row topic-analysis__row--head">
							<div className="topic-analysis__legend">
								{LEGEND.map(legend => (
									<span key={legend.key} className={`topic-analysis__legend-item topic-analysis__legend-item--${legend.key}`}>
										{legend.label}
									</span>
								))}
							</div>
							<span className="topic-analysis__head-cell">Konu Adı</span>
							<span className="topic-analysis__head-cell">Konu Önemi</span>
							<button type="button" className="topic-analysis__sort" onClick={() => setSortAsc(prev => !prev)}>
								Başarı Yüzdesi
								<i className={`icons-alt-arrow-${sortAsc ? 'up' : 'down'}`} />
							</button>
							<span className="topic-analysis__head-cell">İlişkili Sorular</span>
						</div>

						{sortedTopics.map((topic, index) => {
							const items = topic.topicItems ?? [];
							const wrongCount = items.filter(item => item.answerResult === ExamTopicItemAnswerResultEnum.False).length;
							const stars = topic.priority ? PRIORITY_STARS[topic.priority] : 0;

							return (
								<div key={topic.topicId ?? index} className="topic-analysis__row">
									<div className="topic-analysis__cell topic-analysis__cell--name">
										<span className="topic-analysis__index">{index + 1}</span>
										<span className="topic-analysis__topic-name">{topic.topicName}</span>
									</div>
									<div className="topic-analysis__cell topic-analysis__cell--stars">
										<span className="topic-analysis__cell-label">Konu Önemi</span>
										<div className="topic-analysis__stars">
											{Array.from({ length: MAX_STARS }, (_, i) => (
												<i key={i} className={clsx('icons-star', { inactive: i >= stars })} />
											))}
										</div>
									</div>
									<div className="topic-analysis__cell topic-analysis__cell--ring">
										<SuccessRing rate={topic.successRate ?? 0} wrongRatio={items.length ? wrongCount / items.length : 0} />
									</div>
									<div className="topic-analysis__cell topic-analysis__cell--items">
										<span className="topic-analysis__cell-label">İlişkili Sorular</span>
										<div className="topic-analysis__items">
											{items.map(item => (
												<span
													key={item.itemId}
													className={clsx(
														'topic-analysis__item',
														`topic-analysis__item--${item.answerResult ? ANSWER_CLASS[item.answerResult] : 'empty'}`
													)}
												>
													{item.itemOrder}
												</span>
											))}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</Otherwise>
			</Choose>
		</Modal>
	);
};

export default TopicAnalysisModal;
